import { FC } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { InputAdornment, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';

import Tooltip from '../Tooltip';
import Typography from '../Typography';
import { TextFieldProps } from './types';

const ElementsTextField: FC<TextFieldProps> = ({
  'aria-label': ariaLabel,
  autoCapitalize,
  autoCorrect,
  description,
  disabled = false,
  endAdornment,
  error = false,
  label,
  maxLength,
  multiline,
  name,
  onChange,
  onFocus,
  placeholder,
  readOnly,
  required = true,
  requiredLabel = 'Required',
  rows,
  shouldErrorMessageTakeUpSpace = true,
  showRequiredLabel = false,
  startAdornment,
  tooltipContent,
  value,
  ...rest
}) => {
  const theme = useTheme();

  const descriptionContent = typeof error === 'string' ? error : description;
  return (
    <TextField
      fullWidth
      value={value}
      onChange={onChange}
      onFocus={onFocus}
      disabled={disabled}
      error={!!error}
      required={required}
      helperText={descriptionContent}
      placeholder={placeholder}
      multiline={multiline}
      rows={rows}
      InputProps={{
        endAdornment: endAdornment && (
          <InputAdornment position='end'>{endAdornment}</InputAdornment>
        ),
        startAdornment: startAdornment && (
          <InputAdornment position='start'>{startAdornment}</InputAdornment>
        ),
      }}
      inputProps={{
        'aria-label': ariaLabel,
        autoCapitalize,
        autoCorrect,
        label,
        maxLength,
        name,
        readOnly,
      }}
      label={
        <Row>
          <span>{label}</span>
          {tooltipContent && (
            <Tooltip tooltipContent={tooltipContent}>
              <InfoOutlinedIcon fontSize='inherit' />
            </Tooltip>
          )}
          {label && required && showRequiredLabel && (
            <RequiredLabel>
              <Typography
                variant='font14'
                color={theme.palette.colors.grey[700]}
              >
                {requiredLabel}
              </Typography>
            </RequiredLabel>
          )}
        </Row>
      }
      InputLabelProps={{
        style: {
          alignItems: 'center',
          color:
            readOnly || disabled
              ? theme.palette.colors.grey[500]
              : theme.palette.colors.black,
          display: 'flex',
          gap: 8,
          pointerEvents: 'auto',
        },
      }}
      sx={[
        {
          '& .Mui-disabled': readOnly
            ? {}
            : {
                '& .MuiFormHelperText-root': {
                  color: theme.palette.colors.grey[300],
                  whiteSpace: 'break-spaces',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.colors.grey[100],
                },
                '&.MuiOutlinedInput-root': {
                  backgroundColor: theme.palette.colors.slate[100],
                },
                color: theme.palette.colors.grey[400],
              },
          '& .MuiFormHelperText-root': {
            marginLeft: 0,
            whiteSpace: 'break-spaces',
            ...(!shouldErrorMessageTakeUpSpace && {
              height: 0,
              marginTop: 0,
            }),
          },
          '& .MuiInputBase-input': {
            fontFamily: theme.typography.font14.fontFamily,
            fontSize: theme.typography.font14.fontSize,
            fontWeight: theme.typography.font14.fontWeight,
          },
          '& .MuiInputBase-multiline': {
            padding: '10px 0',
          },
          '& .MuiInputBase-root': {
            backgroundColor: theme.palette.colors.white,
          },
          '& .MuiInputBase-root .MuiOutlinedInput-notchedOutline': {
            borderWidth: readOnly ? 0 : 1,
          },
          '& .MuiInputLabel-asterisk': {
            display: 'none',
          },
          '& .MuiInputLabel-root': label
            ? {
                color: readOnly
                  ? theme.palette.colors.grey[500]
                  : theme.palette.colors.black,
                fontFamily: theme.typography.font14.fontFamily,
                fontSize: theme.typography.font14.fontSize,
                fontWeight: 500,
                minHeight: 20,
                padding: `0 0 ${theme.spacing(1)}`,
                position: 'initial',
                transform: 'none',
                whiteSpace: 'normal',
              }
            : {},
          '& .MuiOutlinedInput-input': {
            height: multiline ? undefined : 40,
            minHeight: 40,
            padding: readOnly ? 0 : `0 ${theme.spacing(2)}`,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            '& span': {
              display: 'none',
            },
            borderColor: theme.palette.colors.grey[500],
            borderWidth: readOnly ? 0 : 1,
          },
          '& ::placeholder': {
            color: `${theme.palette.colors.grey[600]} !important`,
            opacity: '1 !important',
          },
          '& :active,.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              borderWidth: readOnly ? 0 : 1,
            },
          },
          '& :hover': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.colors.grey[900],
            },
          },
          fontSize: '14px',
          height: 'fit-content',
        },
        Boolean(label) && {
          '& :not(.Mui-required).MuiInputLabel-root:after': {
            color: theme.palette.colors.grey[700],
            content: '"Optional"',
            marginLeft: '4px',
          },
        },
      ]}
      {...rest}
    />
  );
};

export default ElementsTextField;

const Row = styled('div')`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const RequiredLabel = styled('div')`
  cursor: default;
`;
